<template>
  <!--
    AB#21537 — the navigation rail.

    Desktop: a 64px rail of icons (tooltips on hover, a flyout for sections),
    which the header control expands to 232px with labels and group headings
    for this visit. Every page load starts with the rail. The current page
    carries the brand pill; the group it belongs to shows in the header crumb.

    Phone (≤767px): the rail is gone. A bottom bar holds the first four leaf
    destinations and "More"; More (or the header control) slides up a sheet
    with the whole menu, grouped the same way as the rail.

    The column itself never scrolls (AB#21225): the list is the one scrolling
    region and the account footer is its sibling, a fixed row of the column.
  -->

  <!-- ===================== phone ===================== -->
  <template v-if="mobileDevice">
    <Teleport to="body">
      <nav class="mnav-bar" aria-label="Main" data-test="mobile-nav-bar">
        <component
          :is="tag(e)"
          v-for="e in barEntries"
          :key="e.key"
          v-bind="linkAttrs(e)"
          class="mnav-tab"
          :class="{ 'mnav-tab--on': isCurrent(e) }"
          :aria-current="isCurrent(e) ? 'page' : undefined"
          :data-test="`mnav-${e.key}`"
          @click="onEntryClick(e, $event)"
        >
          <component :is="e.icon" class="mnav-ico" aria-hidden="true" />
          <span>{{ e.label }}</span>
        </component>
        <button
          type="button"
          class="mnav-tab"
          :class="{ 'mnav-tab--on': sidebarOpen }"
          :aria-expanded="sidebarOpen"
          aria-controls="app-sidebar"
          data-test="mnav-more"
          @click="sidebarOpen = !sidebarOpen"
        >
          <component :is="icons.Ellipsis" class="mnav-ico" aria-hidden="true" />
          <span>More</span>
        </button>
      </nav>

      <div
        v-if="sidebarOpen"
        class="mnav-scrim"
        data-test="mnav-scrim"
        @click="sidebarOpen = false"
      ></div>
      <aside
        v-if="sidebarOpen"
        id="app-sidebar"
        class="mnav-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        data-test="mobile-nav-sheet"
        @keydown.esc="sidebarOpen = false"
      >
        <div class="mnav-grab" aria-hidden="true"></div>
        <div class="mnav-sheet-head">
          <span class="mnav-sheet-title">Menu</span>
          <button
            type="button"
            class="mnav-close"
            aria-label="Close menu"
            data-test="mnav-close"
            @click="sidebarOpen = false"
          >
            <component :is="icons.X" class="size-5" aria-hidden="true" />
          </button>
        </div>
        <div class="mnav-sheet-body">
          <template v-for="g in groups" :key="g.id">
            <div v-if="g.entries.length" class="mnav-grp-t">{{ g.title }}</div>
            <template v-for="e in g.entries" :key="e.key">
              <component
                :is="tag(e)"
                v-bind="linkAttrs(e)"
                class="mnav-row"
                :class="{ 'mnav-row--on': isCurrent(e) && !e.children?.length }"
                :aria-current="isCurrent(e) && !e.children?.length ? 'page' : undefined"
                :data-test="`msheet-${e.key}`"
                @click="onEntryClick(e, $event)"
              >
                <component :is="e.icon" class="mnav-row-ico" aria-hidden="true" />
                <span>{{ e.label }}</span>
              </component>
              <a
                v-for="c in e.children"
                :key="c.key"
                :href="c.href"
                class="mnav-row mnav-row--child"
                :class="{ 'mnav-row--on': isCurrentChild(c) }"
                :aria-current="isCurrentChild(c) ? 'page' : undefined"
                :data-test="`msheet-${c.key}`"
                @click="onChildClick(c, $event)"
              >
                <span>{{ c.label }}</span>
              </a>
            </template>
          </template>
        </div>
        <div class="mnav-sheet-foot" data-test="sidebar-account">
          <div class="account" :title="accountName">
            <UserAvatar
              :name="accountName"
              :initials="accountInitials"
              :src="user?.avatar_url"
              :size="32"
              class="account__avatar"
            />
            <span class="account__text">
              <span class="account__name">{{ accountName }}</span>
              <span class="account__role">{{ accountRole }}</span>
            </span>
          </div>
          <button type="button" class="signout" data-test="sidebar-signout" @click="store.logout">
            <ArrowLeftStartOnRectangleIcon class="size-5 shrink-0" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </Teleport>
  </template>

  <!-- ===================== desktop ===================== -->
  <aside
    v-else
    id="app-sidebar"
    ref="sidebarRef"
    class="sidebar-shell rail"
    :class="{ 'rail--open': sidebarOpen }"
    data-test="nav-rail"
    @keydown.esc="flyout = null"
  >
    <router-link to="/" class="rail__logo" :title="sidebarOpen ? undefined : 'Custimoo'">
      <img :src="sidebarOpen ? Logo : MobileLogo" alt="Custimoo" class="rail__logo-img" />
    </router-link>

    <div v-if="loadingNavigation" class="rail__loading">
      <Loader :loading="loadingNavigation" />
    </div>

    <!--
      The scrolling region, and the only thing in this column that scrolls.
      It holds list items and nothing else: group headings and separators are
      list items too, so a screen reader never meets a list with strays in it.
    -->
    <ul v-else class="navigation-list" :aria-label="'Main'">
      <template v-for="(g, gi) in groups" :key="g.id">
        <li
          v-if="g.entries.length && gi > 0"
          class="rail__sep"
          role="separator"
          aria-hidden="true"
        ></li>
        <li v-if="g.entries.length" class="rail__grp-t" aria-hidden="true">{{ g.title }}</li>
        <li
          v-for="e in g.entries"
          :key="e.key"
          class="rail__item"
          :class="{ 'rail__item--on': isCurrent(e), 'rail__item--fly': flyout === e.key }"
          @mouseenter="onEnter(e, $event)"
          @mouseleave="onLeave(e)"
          @focusin="onEnter(e, $event)"
          @focusout="onFocusOut(e, $event)"
        >
          <component
            :is="e.children?.length ? 'button' : tag(e)"
            v-bind="
              e.children?.length
                ? {
                    type: 'button',
                    'aria-haspopup': 'true',
                    'aria-expanded': flyout === e.key || (sidebarOpen && openKey === e.key),
                  }
                : linkAttrs(e)
            "
            class="rail__btn"
            :aria-current="isCurrent(e) && !e.children?.length ? 'page' : undefined"
            :data-test="`nav-${e.key}`"
            @click="e.children?.length ? onSectionClick(e, $event) : onEntryClick(e, $event)"
          >
            <component :is="e.icon" class="rail__ico" aria-hidden="true" />
            <span class="rail__lbl">{{ e.label }}</span>
            <ChevronRightIcon
              v-if="e.children?.length"
              class="rail__chev"
              :class="{ 'rail__chev--open': openKey === e.key }"
              aria-hidden="true"
            />
          </component>

          <!-- expanded: the section's pages inline -->
          <ul
            v-if="sidebarOpen && e.children?.length && openKey === e.key"
            class="rail__sub"
            :data-test="`nav-sub-${e.key}`"
          >
            <li v-for="c in e.children" :key="c.key">
              <a
                :href="c.href"
                class="rail__sub-a"
                :class="{ 'rail__sub-a--on': isCurrentChild(c) }"
                :aria-current="isCurrentChild(c) ? 'page' : undefined"
                :data-test="`nav-${c.key}`"
                @click="onChildClick(c, $event)"
              >
                {{ c.label }}
              </a>
            </li>
          </ul>
        </li>
      </template>
    </ul>

    <!--
      Tooltip and flyout live outside the column: the list scrolls, and a
      scrolling box clips anything that hangs out of it. They are positioned
      against the hovered item, like the row menus are.
    -->
    <Teleport to="body">
      <span
        v-if="!sidebarOpen && tip"
        class="rail__tip"
        role="tooltip"
        :style="{ top: `${tip.top}px`, left: `${tip.left}px` }"
        data-test="nav-tip"
      >
        {{ tip.label }}
      </span>
      <div
        v-if="!sidebarOpen && flyEntry"
        class="rail__fly"
        role="group"
        :aria-label="flyEntry.label"
        :style="{ top: `${flyRect.top}px`, left: `${flyRect.left}px` }"
        :data-test="`nav-fly-${flyEntry.key}`"
        @mouseenter="onEnter(flyEntry)"
        @mouseleave="onLeave(flyEntry)"
        @keydown.esc="flyout = null"
      >
        <div class="rail__fly-h">{{ flyEntry.label }}</div>
        <a
          v-for="c in flyEntry.children"
          :key="c.key"
          :href="c.href"
          class="rail__fly-a"
          :class="{ 'rail__fly-a--on': isCurrentChild(c) }"
          :aria-current="isCurrentChild(c) ? 'page' : undefined"
          :data-test="`nav-${c.key}`"
          @click="onChildClick(c, $event)"
        >
          {{ c.label }}
        </a>
      </div>
    </Teleport>

    <!--
      The account footer (AB#21225): who you are signed in as first, the way
      out second. A SIBLING of the list, so it cannot be pushed off screen.
    -->
    <div v-if="!loadingNavigation" class="sidebar__foot" data-test="sidebar-account">
      <div
        class="account"
        :class="{ 'account--collapsed': !sidebarOpen }"
        :title="sidebarOpen ? undefined : accountName"
      >
        <UserAvatar
          :name="accountName"
          :initials="accountInitials"
          :src="user?.avatar_url"
          :size="32"
          class="account__avatar"
        />
        <span v-if="sidebarOpen" class="account__text">
          <span class="account__name">{{ accountName }}</span>
          <span class="account__role">{{ accountRole }}</span>
        </span>
      </div>
      <button
        type="button"
        class="signout"
        :class="{ 'signout--collapsed': !sidebarOpen }"
        data-test="sidebar-signout"
        :title="sidebarOpen ? 'Sign out' : `Sign out of ${accountName}`"
        :aria-label="sidebarOpen ? undefined : 'Sign out'"
        @click="store.logout"
      >
        <ArrowLeftStartOnRectangleIcon class="size-5 shrink-0" />
        <span v-if="sidebarOpen">Sign out</span>
      </button>
    </div>
    <slot />
  </aside>
</template>

<script setup lang="ts">
import MobileLogo from '@/assets/img/avatars/custimoo-logo.png';
import Logo from '@/assets/img/images/web-logo.png';
import Loader from '@/components/Shared/Loader.vue';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import { can, logError } from '@/composables';
import { resolveMenuHref } from '@/composables/useMenuHref';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { NavigationMenuItem } from '@/models';
import { useMainStore } from '@/store';
import { ArrowLeftStartOnRectangleIcon, ChevronRightIcon } from '@heroicons/vue/24/solid';
import { icons } from 'lucide-vue-next';
import { deskMessagesManage } from '@/composables/useDeskMessages';
import { storeToRefs } from 'pinia';
import { computed, defineProps, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { NAV_GROUPS, groupIdFor, isCurrentPath, type NavGroupId } from '@/utils/navRail';

const appURL = import.meta.env.VITE_VUE2_APP_URL;
const tdsLink = import.meta.env.VITE_APP_TDS_LINK;

/** Sidebar entries for screens this app owns stay in-app; the rest go to the legacy admin. */
const menuHref = (path: string | null): string => resolveMenuHref(path, appURL);

const props = defineProps<{ navigation: NavigationMenuItem[] }>();

const store = useMainStore();
const sidebarRef = ref<HTMLElement | null>(null);
const { mobileDevice, sidebarOpen, loadingNavigation, permissions, user, role, globalLoader } =
  storeToRefs(useMainStore());
const route = useRoute();
const access_token = localStorage.getItem('access_token');

/* ------------------------------------------------------------------ *
 * Entries
 * ------------------------------------------------------------------ */

interface NavChild {
  key: string;
  label: string;
  href: string;
  /** In-app path when this app owns the screen. */
  path: string | null;
}

interface NavEntry {
  key: string;
  label: string;
  icon: object;
  /** In-app route (router-link). */
  to?: string;
  /** Plain link (legacy admin, external tool). */
  href?: string;
  /** Runs instead of navigating. */
  action?: () => void;
  children?: NavChild[];
  group: NavGroupId;
}

const iconFor = (id: unknown): object =>
  typeof id === 'string' && id in icons ? (icons as Record<string, object>)[id] : icons.List;

const slug = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const canSeeTracking = computed<boolean>(() => can('view-tracking-dashboard'));
// AB#20740 — any of the three cumulative shipping permissions.
const canSeeShipping = computed<boolean>(() =>
  can(['view-shipping', 'operate-shipping', 'manage-shipping'])
);

// staging-only: AI Analytics entry (AiAnalyticsReport, b10d681). Merchants and
// anyone attached to a company; SuperAdmin always.
const canAccessAiAnalytics = computed<boolean>(() => {
  const roleName = role?.value?.name?.replace(/\s+/g, '').toLowerCase() ?? '';
  if (roleName === 'superadmin') return true;
  if (roleName.includes('merchant')) return true;
  return Boolean(user?.value?.company_id);
});

const childOf = (parentKey: string, item: NavigationMenuItem): NavChild => ({
  key: `${parentKey}-${slug(item.name)}`,
  label: item.name,
  href: menuHref(item.to),
  path: item.to,
});

/** Every entry this person may see, in menu order, before grouping. */
const entries = computed<NavEntry[]>(() => {
  const list: NavEntry[] = [];
  if (can('access-new-dashboard')) {
    list.push({
      key: 'dashboard',
      label: 'Dashboard',
      icon: icons.LayoutDashboard,
      to: '/dashboard',
      group: 'work',
    });
    // AB#22512 — the orders held to ship together; same audience as the Dashboard.
    list.push({
      key: 'consolidation-waiting',
      label: 'Waiting for consolidation',
      icon: icons.PackageCheck,
      to: '/orders/waiting-for-consolidation',
      group: 'work',
    });
  }
  for (const item of props.navigation ?? []) {
    const key = slug(item.name);
    const children = (item.children ?? []).map((c) => childOf(key, c));
    list.push({
      key,
      label: item.name,
      icon: iconFor(item.icon_id),
      href: children.length ? undefined : menuHref(item.to),
      children: children.length ? children : undefined,
      group: groupIdFor(item.name),
    });
  }
  if (can('view-companies-module')) {
    list.push({
      key: 'companies',
      label: 'Companies',
      icon: icons.Building2,
      group: 'catalogue',
      children: [
        {
          key: 'companies-all',
          label: 'All Companies',
          href: menuHref('/companies'),
          path: '/companies',
        },
      ],
    });
  }
  if (canSeeTracking.value)
    list.push({
      key: 'tracking',
      label: 'Tracking',
      icon: icons.Truck,
      to: '/tracking',
      group: 'work',
    });
  // AB#20740 — Shipping (logistics dashboard), for any of the three cumulative
  // shipping permissions; the routes and the API check the same.
  if (canSeeShipping.value)
    list.push({
      key: 'shipping',
      label: 'Shipping',
      icon: icons.Plane,
      to: '/shipping/queue',
      group: 'work',
    });
  // Restore deleted order: undo Delete order (restore-order, Super Admin).
  if (can('restore-order'))
    list.push({
      key: 'restore-order',
      label: 'Restore deleted order',
      icon: icons.ArchiveRestore,
      to: '/orders/restore',
      group: 'work',
    });
  if (can('change-order-type'))
    list.push({
      key: 'order-type',
      label: 'Order Type',
      icon: icons.Repeat2,
      to: '/order-type-change',
      group: 'work',
    });
  if (can('delivery-boxes'))
    list.push({
      key: 'delivery-box',
      label: 'Delivery Box',
      icon: icons.Boxes,
      to: '/delivery-box',
      group: 'work',
    });
  if (can('generate-barcodes'))
    list.push({
      key: 'barcode',
      label: 'Barcode',
      icon: icons.Barcode,
      to: '/order-barcode',
      group: 'catalogue',
    });
  if (canAccessAiAnalytics.value)
    list.push({
      key: 'ai-analytics',
      label: 'AI Analytics',
      icon: icons.ChartColumn,
      to: '/ai-analytics',
      group: 'catalogue',
    });
  if (permissions.value?.includes('generate-drawing-sheet')) {
    list.push({
      key: 'drawing-sheet',
      label: 'Generate Drawing Sheet',
      icon: icons.Sheet,
      href: `${tdsLink}/?auth=${access_token}`,
      group: 'setup',
    });
  }
  // AB#21903 — Team: the lead order administrator's home (Flagged, Load,
  // Mood — one tab per permission; any one of them shows the entry).
  if (
    can('view-team-needs-attention') ||
    can('view-finance-team-needs-attention') ||
    can('view-team-load') ||
    can('view-team-communication-mood') ||
    can('view-forecast')
  ) {
    list.push({
      key: 'team',
      label: 'Team',
      icon: icons.Users,
      to: '/team',
      group: 'work',
    });
  }
  // AB#21902 — Accounts: the CSM's home page (own companies; every company
  // with view-account-health-team). AB#21951 — it lives in the CRM section,
  // which needs view-crm as well as the page's own permission.
  if (can('view-crm') && can('view-account-health')) {
    list.push({
      key: 'accounts',
      label: 'Accounts',
      icon: icons.Building2,
      to: '/accounts',
      group: 'crm',
    });
  }
  // AB#21971 — Quotes: the quote pipeline (stages, follow-ups, win rate).
  if (can('view-crm') && can('view-crm-quotes')) {
    list.push({
      key: 'quotes',
      label: 'Quotes',
      icon: icons.FileText,
      to: '/crm/quotes',
      group: 'crm',
    });
  }
  // AB#21959 — Kickback & Commission moved from Companies into CRM: buying
  // groups and external sales reps are sales relationships.
  if (can('view-crm') && can('view-kickback-commission')) {
    list.push({
      key: 'kickback',
      label: 'Kickback & Commission',
      icon: icons.Percent,
      to: '/buying-groups-sales-reps',
      group: 'crm',
    });
  }
  // AB#22389 — Finance → Mark orders invoiced: invoices raised outside Stripe.
  // This tier has no Finance section yet, so it sits under Work.
  if (can('mark-orders-invoiced')) {
    list.push({
      key: 'mark-invoiced',
      label: 'Mark orders invoiced',
      icon: icons.ReceiptText,
      to: '/finance/mark-invoiced',
      group: 'work',
    });
  }
  // AB#21842 — Ops Command Center: the full-screen wall (meant for a TV), feature-gated.
  if (store.features?.command_center === true) {
    list.push({
      key: 'command-center',
      label: 'Command Center',
      icon: icons.MonitorPlay,
      href: '/command-center',
      group: 'work',
    });
  }
  // AB#21904 / AB#21908 — Setup → Agents (ELSA, ATLAS): quality, outcomes and
  // the dials, one tab per permission; any one of them shows the entry.
  if (
    can('view-elsa-quality') ||
    can('view-elsa-outcomes') ||
    can('manage-elsa-settings') ||
    can('view-atlas-quality') ||
    can('manage-atlas-settings')
  ) {
    list.push({
      key: 'agents-setup',
      label: 'Agents',
      icon: icons.Sparkles,
      to: '/setup/agents',
      group: 'setup',
    });
  }
  // AB#22158 — Setup → Copy lockers & shops between customer accounts.
  if (can('copy-customer-content')) {
    list.push({
      key: 'account-copy',
      label: 'Copy lockers & shops',
      icon: icons.Copy,
      to: '/setup/copy-lockers-shops',
      group: 'setup',
    });
  }
  // AB#21836 — Desk messages: senders only (feature flag from the login payload).
  if (deskMessagesManage(store.features)) {
    list.push({
      key: 'desk-messages',
      label: 'Desk messages',
      icon: icons.Megaphone,
      to: '/desk-messages',
      group: 'setup',
    });
  }
  if (user.value && permissions.value?.includes('stripe-portal-access')) {
    list.push({
      key: 'invoice-portal',
      label: 'Invoice Portal',
      icon: icons.Receipt,
      action: handleInvoicePortalClick,
      group: 'setup',
    });
  }
  return list;
});

const groups = computed(() =>
  NAV_GROUPS.map((g) => ({ ...g, entries: entries.value.filter((e) => e.group === g.id) }))
);

/** The bottom bar: the first four leaf destinations in group order — Work first. */
const barEntries = computed(() =>
  groups.value
    .flatMap((g) => g.entries)
    .filter((e) => !e.children?.length && !e.action)
    .slice(0, 4)
);

/* ------------------------------------------------------------------ *
 * Current page
 * ------------------------------------------------------------------ */

const currentPath = computed(() => route?.path ?? '');

const isCurrentChild = (c: NavChild): boolean => isCurrentPath(currentPath.value, c.path);
const isCurrent = (e: NavEntry): boolean =>
  e.children?.length
    ? e.children.some(isCurrentChild)
    : isCurrentPath(currentPath.value, e.to ?? null);

/* ------------------------------------------------------------------ *
 * Open section (expanded) and flyout (collapsed)
 * ------------------------------------------------------------------ */

const openKey = ref<string | null>(null);
const flyout = ref<string | null>(null);
const flyRect = ref({ top: 0, left: 0 });
const tip = ref<{ label: string; top: number; left: number } | null>(null);
let leaveTimer: ReturnType<typeof setTimeout> | undefined;

const flyEntry = computed(
  () => entries.value.find((e) => e.key === flyout.value && e.children?.length) ?? null
);

/** Where a tooltip or flyout sits: beside the item, in viewport coordinates. */
const anchorOf = (ev?: Event) => {
  const el = ev?.currentTarget as HTMLElement | undefined;
  const r = el?.getBoundingClientRect();
  return r
    ? { top: r.top, left: r.right + 8, mid: r.top + r.height / 2 }
    : { top: 0, left: 0, mid: 0 };
};

// The section holding the current page starts open, so the menu says where you are.
watch(
  [entries, currentPath, sidebarOpen],
  () => {
    const holder = entries.value.find((e) => e.children?.length && isCurrent(e));
    if (holder && openKey.value === null) openKey.value = holder.key;
  },
  { immediate: true }
);

const onSectionClick = (e: NavEntry, ev?: Event) => {
  if (sidebarOpen.value) {
    openKey.value = openKey.value === e.key ? null : e.key;
  } else if (flyout.value === e.key) {
    flyout.value = null;
  } else {
    const a = anchorOf(ev);
    flyRect.value = { top: a.top - 6, left: a.left };
    flyout.value = e.key;
  }
};
const onEnter = (e: NavEntry, ev?: Event) => {
  clearTimeout(leaveTimer);
  if (sidebarOpen.value) return;
  const a = anchorOf(ev);
  if (e.children?.length) {
    if (ev) flyRect.value = { top: a.top - 6, left: a.left };
    flyout.value = e.key;
    tip.value = null;
  } else if (ev) {
    tip.value = { label: e.label, top: a.mid, left: a.left };
  }
};
const onLeave = (e: NavEntry) => {
  tip.value = null;
  if (flyout.value !== e.key) return;
  // A short grace so the pointer can cross the gap into the flyout.
  leaveTimer = setTimeout(() => {
    if (flyout.value === e.key) flyout.value = null;
  }, 120);
};
const onFocusOut = (e: NavEntry, ev: FocusEvent) => {
  const next = ev.relatedTarget as HTMLElement | null;
  const item = ev.currentTarget as HTMLElement;
  tip.value = null;
  if (flyout.value === e.key && !(next && (item.contains(next) || next.closest('.rail__fly'))))
    flyout.value = null;
};

/* ------------------------------------------------------------------ *
 * Links
 * ------------------------------------------------------------------ */

const tag = (e: NavEntry): string => (e.to ? 'router-link' : e.href ? 'a' : 'button');
const linkAttrs = (e: NavEntry): Record<string, unknown> =>
  e.to ? { to: e.to } : e.href ? { href: e.href } : { type: 'button' };

const closeAfterNavigate = () => {
  flyout.value = null;
  if (mobileDevice.value) sidebarOpen.value = false;
};
const onEntryClick = (e: NavEntry, ev: Event) => {
  if (e.action) {
    ev.preventDefault();
    e.action();
  }
  closeAfterNavigate();
};
const onChildClick = (c: NavChild, ev: Event) => {
  void c;
  void ev;
  closeAfterNavigate();
};

// A function declaration: `entries` refers to it from a watcher that runs during setup.
async function handleInvoicePortalClick() {
  try {
    globalLoader.value = true;
    const response = await http.get(`/stripe-billing-portal?redirect_url_v2=true`);
    globalLoader.value = false;
    const portalLink = response.data?.result?.billing_portal_link;
    if (portalLink) {
      window.open(portalLink, '_self');
    } else {
      logError('No billing portal link returned from API');
    }
  } catch (error) {
    globalLoader.value = false;
    logError('Error fetching the PDF URL:', error);
  }
}

/* ------------------------------------------------------------------ *
 * Account footer (AB#21225)
 * ------------------------------------------------------------------ */

const accountName = computed<string>(() =>
  (user.value?.name || user.value?.email || 'Signed in').trim()
);
const accountRole = computed<string>(() => store.role?.name || 'No role assigned');
const accountInitials = computed<string>(() => {
  const named = (user.value?.name || '').trim();
  const fromEmail = (user.value?.email || '').split('@')[0] ?? '';
  const source = named || fromEmail || '?';
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const letters =
    words.length > 1
      ? [words[0], words[words.length - 1]].map((w) => Array.from(w)[0])
      : Array.from(words[0] ?? '?').slice(0, 2);
  return letters.join('').toUpperCase() || '?';
});
</script>

<style scoped>
/* ---------- the column (AB#21225: it never scrolls; the list does) ---------- */
.sidebar-shell {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: visible;
}
.rail {
  width: 64px;
  min-width: 64px;
  background: #16181d;
  color: #c9ced6;
  padding: 10px 10px 12px;
  gap: 4px;
  transition:
    width 0.18s ease,
    min-width 0.18s ease;
  position: relative;
  z-index: 10025;
}
.rail--open {
  width: 232px;
  min-width: 232px;
}
.rail__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.rail__logo-img {
  max-height: 32px;
  width: auto;
}
.rail__loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.navigation-list {
  list-style: none;
  margin: 0;
  padding: 0 0 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: visible;
  scrollbar-width: none;
}
.navigation-list::-webkit-scrollbar {
  display: none;
}
.rail__sep {
  height: 1px;
  background: #262a31;
  margin: 8px 8px;
}
.rail--open .rail__sep {
  display: none;
}
.rail__grp-t {
  display: none;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7c838f;
  padding: 12px 10px 4px;
  font-weight: 600;
}
.rail--open .rail__grp-t {
  display: block;
}
.rail__item {
  position: relative;
}
.rail__btn {
  position: relative;
  width: 44px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #c9ced6;
  background: transparent;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  padding: 0;
  text-decoration: none;
  transition:
    background 0.12s,
    color 0.12s;
}
.rail--open .rail__btn {
  width: 100%;
  justify-content: flex-start;
  padding: 0 10px;
}
.rail__btn:hover,
.rail__item--fly > .rail__btn {
  background: #22252c;
  color: #fff;
}
.rail__btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.rail__item--on > .rail__btn {
  background: var(--accent);
  color: #fff;
}
.rail__ico {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
.rail__lbl {
  display: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rail--open .rail__lbl {
  display: inline;
}
.rail__chev {
  display: none;
  margin-left: auto;
  width: 14px;
  height: 14px;
  color: #7c838f;
  transition: transform 0.12s;
}
.rail--open .rail__chev {
  display: inline;
}
.rail__chev--open {
  transform: rotate(90deg);
}
</style>

<style>
/* AB#21537 — tooltip and flyout are teleported to <body>, so these are unscoped. */
.rail__tip {
  position: fixed;
  transform: translateY(-50%);
  z-index: 10028;
  background: #1c1d1f;
  color: #fff;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}
.rail__fly {
  position: fixed;
  z-index: 10028;
  min-width: 190px;
  background: var(--surface);
  color: var(--ink);
  border-radius: 10px;
  padding: 6px;
  box-shadow: 0 12px 32px rgba(15, 18, 24, 0.22);
  border: 1px solid var(--border);
}
.rail__fly::before {
  /* the bridge across the gap, so the pointer never "leaves" on the way over */
  content: '';
  position: absolute;
  left: -10px;
  top: 0;
  bottom: 0;
  width: 10px;
}
.rail__fly-h {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  padding: 6px 10px 4px;
  font-weight: 600;
}
.rail__fly-a {
  display: block;
  padding: 7px 10px;
  border-radius: 6px;
  color: var(--ink);
  text-decoration: none;
  font-size: 13px;
  white-space: nowrap;
}
.rail__fly-a:hover,
.rail__fly-a:focus-visible {
  background: var(--surface-2);
  outline: none;
}
.rail__fly-a--on,
.rail__fly-a--on:hover {
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
}
</style>

<style scoped>
/* inline sub-list: expanded only */
.rail__sub {
  list-style: none;
  margin: 2px 0 4px;
  padding: 0 0 0 30px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.rail__sub-a {
  display: block;
  color: #c9ced6;
  text-decoration: none;
  font-size: 12.5px;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rail__sub-a:hover,
.rail__sub-a:focus-visible {
  background: #22252c;
  color: #fff;
  outline: none;
}
.rail__sub-a--on {
  color: #fff;
  font-weight: 600;
  background: #22252c;
}

/* ---------- account footer ---------- */
.sidebar__foot {
  flex-shrink: 0;
  border-top: 1px solid #262a31;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.account {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
  min-width: 0;
}
.account--collapsed {
  justify-content: center;
  padding: 6px 0;
}
.account__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.account__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.account__name {
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account__role {
  color: #7c838f;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.signout {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: #c9ced6;
  font: inherit;
  font-size: 12.5px;
  cursor: pointer;
}
.signout--collapsed {
  justify-content: center;
  padding: 0;
}
.signout:hover,
.signout:focus-visible {
  background: #3a1f1f;
  color: #ff9a8f;
  outline: none;
}

/* ---------- phone ---------- */
.mnav-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10025;
  height: calc(60px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: var(--surface);
  border-top: 1px solid var(--border);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  align-items: center;
}
.mnav-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 60px;
  font: inherit;
  font-size: 10.5px;
  color: var(--ink-mute);
  background: transparent;
  border: 0;
  text-decoration: none;
  cursor: pointer;
}
.mnav-tab--on {
  color: var(--accent);
  font-weight: 600;
}
.mnav-ico {
  width: 22px;
  height: 22px;
}
.mnav-scrim {
  position: fixed;
  inset: 0;
  z-index: 10026;
  background: rgba(20, 22, 26, 0.4);
}
.mnav-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10027;
  max-height: 82vh;
  background: var(--surface);
  color: var(--ink);
  border-radius: 18px 18px 0 0;
  box-shadow: 0 -12px 32px rgba(15, 18, 24, 0.18);
  display: flex;
  flex-direction: column;
  padding: 8px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  animation: mnav-up 0.18s ease-out;
}
@keyframes mnav-up {
  from {
    transform: translateY(24px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .mnav-sheet {
    animation: none;
  }
  .rail,
  .rail__btn,
  .rail__tip {
    transition: none;
  }
}
.mnav-grab {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: var(--border);
  margin: 0 auto 6px;
}
.mnav-sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px 6px;
}
.mnav-sheet-title {
  font-weight: 700;
  font-size: 15px;
}
.mnav-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 0;
  background: var(--surface-2);
  color: var(--ink-soft);
  display: grid;
  place-items: center;
  cursor: pointer;
}
.mnav-sheet-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mnav-grp-t {
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  font-weight: 600;
  padding: 12px 8px 4px;
}
.mnav-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  color: var(--ink);
  text-decoration: none;
  background: transparent;
  border: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
}
.mnav-row--child {
  padding-left: 42px;
  font-size: 13.5px;
  color: var(--ink-soft);
}
.mnav-row--on {
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
}
.mnav-row-ico {
  width: 20px;
  height: 20px;
  color: var(--ink-soft);
  flex-shrink: 0;
}
.mnav-row--on .mnav-row-ico {
  color: var(--accent);
}
.mnav-sheet-foot {
  border-top: 1px solid var(--border);
  margin-top: 8px;
  padding-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.mnav-sheet-foot .account__name {
  color: var(--ink);
}
.mnav-sheet-foot .signout {
  width: auto;
  color: var(--danger);
}
.mnav-sheet-foot .signout:hover {
  background: var(--danger-soft);
  color: var(--danger);
}
</style>
